import { compose } from 'lodash/fp'
import React from 'react'
import {
  BrowserRouter,
  Redirect,
  Route,
  Router,
  Switch,
} from 'react-router-dom'
import createHistory from 'history/createBrowserHistory'
import ReactGA from 'react-ga'
import { NotificationsService } from '../containers/Notifications'
import withLazyLoading, { withLazyLoadingScope } from './LazyRoute'
import withProtected from './withProtected'
import withTemplate from './withTemplate'
import PasswordReset from '../screens/PasswordReset'
import PasswordResetToken from '../screens/PasswordResetToken'
import {
  CreateReservationModal,
  UpdateReservationModal,
} from '../containers/BookingCalendar/ReservationModal'
import ReservationForms from '../containers/Form/CreateReservation'
import { useSelector } from 'react-redux'
import {
  getUserId,
  getWorkerId,
} from '../reducers/userAccount/selectors/userAccount'
import { PermissionScope } from '../containers/Employees'
import { getIsMultiPriceEnabled } from '../reducers/userObject/selectors/settings'
import {
  CookiesModalStandaloneStep0,
  CookiesModalStandaloneStep1,
} from '../containers/Cookies/CookiesModalStandalone'

export enum TemplateType {
  Old = 1,
  New = 2,
}

const protectedTemplate = compose(
  withProtected('/login'),
  withTemplate,
  withLazyLoadingScope,
)

const RedirectRoute = to => () => <Redirect to={to} />

const Login = withLazyLoading('Login')
const SSOLogin = withLazyLoading('SSOLogin')
const BookingCalendar = protectedTemplate('BookingCalendar')
const Profile = protectedTemplate('Profile', [
  PermissionScope.SCOPE_CAN_USE_PROFILE,
])
const Settings = protectedTemplate('Settings')
const Finance = protectedTemplate('FinanceReport', [
  PermissionScope.SCOPE_CAN_USE_FINANCIAL_REPORTS,
])
const RoomReport = protectedTemplate('RoomReport', [
  PermissionScope.SCOPE_CAN_USE_FINANCIAL_REPORTS,
])
//const Test = protectedTemplate('TestReport')
const BookingLatest = protectedTemplate('BookingLatest', [
  PermissionScope.SCOPE_CAN_VIEW_LIST_RESERVATIONS,
])
const BookingCancelled = protectedTemplate('BookingCancelled', [
  PermissionScope.SCOPE_CAN_VIEW_LIST_RESERVATIONS,
])

const Premium = protectedTemplate(
  'Premium',
  [PermissionScope.SCOPE_CAN_BUY_PREMIUM],
  TemplateType.New,
)
const BookingNotifications = protectedTemplate('BookingNotifications', [
  PermissionScope.SCOPE_CAN_USE_WIDGET,
])
const Clients = protectedTemplate('Clients', [
  PermissionScope.SCOPE_CAN_USE_LIST_CLIENTS,
])
const OfferDescription = protectedTemplate('OfferDescription', [
  PermissionScope.SCOPE_CAN_USE_WIDGET,
])
const OfferRegulation = protectedTemplate('OfferRegulation', [
  PermissionScope.SCOPE_CAN_USE_WIDGET,
])
const OfferPhotos = protectedTemplate('OfferPhotos', [
  PermissionScope.SCOPE_CAN_USE_WIDGET,
])
// const Price = protectedTemplate('Price', [
//   PermissionScope.SCOPE_CAN_USE_PRICING_SETTINGS,
// ])
const PricePrepayment = protectedTemplate('PricePrepayment', [
  PermissionScope.SCOPE_CAN_USE_PRICING_SETTINGS,
])
const PriceMethod = protectedTemplate('PriceMethod', [
  PermissionScope.SCOPE_CAN_USE_WIDGET,
])
const PriceAdditional = protectedTemplate('PriceAdditional', [
  PermissionScope.SCOPE_CAN_USE_PRICING_SETTINGS,
])
const StatsReservations = protectedTemplate('StatsReservations', [
  PermissionScope.SCOPE_CAN_USE_OCCUPANCY_REPORTS,
])
const Widget = protectedTemplate('Widget', [
  PermissionScope.SCOPE_CAN_USE_WIDGET,
])
const WidgetInfo = protectedTemplate('WidgetInfo', [
  PermissionScope.SCOPE_CAN_USE_WIDGET,
])

const IntegrationBooking = protectedTemplate(
  'IntegrationBooking',
  [PermissionScope.SCOPE_CAN_USE_INTEGRATIONS_SETTINGS],
  TemplateType.New,
)

const IntegrationBooking2 = protectedTemplate(
  'IntegrationBooking2',
  [PermissionScope.SCOPE_CAN_USE_INTEGRATIONS_SETTINGS],
  TemplateType.New,
)

const IntegrationBookingInfo = protectedTemplate('IntegrationBookingInfo', [
  PermissionScope.SCOPE_CAN_USE_INTEGRATIONS_SETTINGS,
])
const IntegrationIcalendar = protectedTemplate('IntegrationIcalendar', [
  PermissionScope.SCOPE_CAN_USE_INTEGRATIONS_SETTINGS,
])
const IntegrationIcalendarInfo = protectedTemplate('IntegrationIcalendarInfo', [
  PermissionScope.SCOPE_CAN_USE_INTEGRATIONS_SETTINGS,
])
const IntegrationAirbnb = protectedTemplate('IntegrationAirbnb', [
  PermissionScope.SCOPE_CAN_USE_INTEGRATIONS_SETTINGS,
])
const IntegrationAirbnbInfo = protectedTemplate('IntegrationAirbnbInfo', [
  PermissionScope.SCOPE_CAN_USE_INTEGRATIONS_SETTINGS,
])
const IntegrationBoxBuy = protectedTemplate('IntegrationBoxBuy', [
  PermissionScope.SCOPE_CAN_USE_INTEGRATIONS_SETTINGS,
])
const AirbnbRoomEdit = protectedTemplate('AirbnbRoomEdit', [
  PermissionScope.SCOPE_CAN_USE_INTEGRATIONS_SETTINGS,
])
const MessageTemplates = protectedTemplate('MessageTemplate', [
  PermissionScope.SCOPE_CAN_USE_TEMPLATES,
])
const PasswordChange = protectedTemplate('Password')
const Taxes = protectedTemplate('Taxes', [
  PermissionScope.SCOPE_CAN_USE_INVOICES_SETTINGS,
])
const TaxRates = protectedTemplate('TaxRates', [
  PermissionScope.SCOPE_CAN_USE_INVOICES_SETTINGS,
])
const ReservationsList = protectedTemplate('ReservationsList', [
  PermissionScope.SCOPE_CAN_VIEW_LIST_RESERVATIONS,
])
const ConfigurationPrice = protectedTemplate(
  'ConfigurationPrice',
  [PermissionScope.SCOPE_CAN_USE_PRICING_SETTINGS],
  TemplateType.Old,
)

const ConfigurationPriceNew = protectedTemplate(
  'PriceSettings',
  [PermissionScope.SCOPE_CAN_USE_PRICING_SETTINGS],
  TemplateType.New,
)

const PriceGrid = protectedTemplate('PriceGrid', [
  PermissionScope.SCOPE_CAN_USE_PRICING_AND_RESTRICTIONS,
])

const PriceGridNew = protectedTemplate(
  'PriceGrid2',
  [PermissionScope.SCOPE_CAN_USE_PRICING_AND_RESTRICTIONS],
  TemplateType.New,
)

const ExportReservation = protectedTemplate('ExportReservation', [
  PermissionScope.SCOPE_CAN_USE_EXPORT,
])
const Tasks = protectedTemplate('Tasks', [
  [
    PermissionScope.SCOPE_CAN_USE_TASKS_CLEANING,
    PermissionScope.SCOPE_CAN_USE_TASKS_FOOD,
    PermissionScope.SCOPE_CAN_USE_TASKS_PAYMENTS,
    PermissionScope.SCOPE_CAN_USE_TASKS_INCOMING,
  ],
])
const Facilities = protectedTemplate('Facilities', [
  PermissionScope.SCOPE_CAN_USE_WIDGET,
])
const InvoiceInfo = protectedTemplate('InvoiceInfo')
const BookingReport = protectedTemplate('BookingReport', [
  PermissionScope.SCOPE_CAN_USE_FINANCIAL_REPORTS,
])
const PaymentsReport = protectedTemplate('PaymentsReport', [
  PermissionScope.SCOPE_CAN_USE_FINANCIAL_REPORTS,
])
const Employees = protectedTemplate('Employees', [
  PermissionScope.SCOPE_CAN_USE_PERMISSIONS,
])
const EmployeesInfo = protectedTemplate('EmployeesInfo', [
  PermissionScope.SCOPE_CAN_USE_PERMISSIONS,
])

const RentLicenses = protectedTemplate('RentLicenses', [
  PermissionScope.SCOPE_CAN_USE_WIDGET,
])
const TaxReport = protectedTemplate('TaxReport', [
  PermissionScope.SCOPE_CAN_USE_FINANCIAL_REPORTS,
])
const Overview = protectedTemplate(
  'Overview',
  [PermissionScope.SCOPE_CAN_USE_FINANCIAL_REPORTS],
  TemplateType.New,
)

const AccessDenied = withLazyLoading('AccessDenied')

const BookingMulti = protectedTemplate(
  'IntegrationBookingMulti',
  [PermissionScope.SCOPE_CAN_USE_INTEGRATIONS_SETTINGS],
  TemplateType.New,
)

const BookingMulti2 = protectedTemplate(
  'IntegrationBookingMulti2',
  [PermissionScope.SCOPE_CAN_USE_INTEGRATIONS_SETTINGS],
  TemplateType.New,
)

ReactGA.initialize('UA-37955877-4')

export const history = createHistory()

history.listen(location => {
  ReactGA.set({ page: location.pathname })
  ReactGA.pageview(location.pathname)
})

const Routes = {
  '/': BookingCalendar,
  '/premium': Premium,
  '/booking/calendar': BookingCalendar,
  '/booking/cancelled': BookingCancelled,
  '/booking/export': ExportReservation,
  '/booking/latest': BookingLatest,
  '/booking/notifications': BookingNotifications,
  '/booking/reservations': ReservationsList,
  '/clients': Clients,
  '/finance/report': Finance,
  '/integration/booking': IntegrationBooking,
  '/integration/booking-connect': BookingMulti,
  '/integration/booking-info': IntegrationBookingInfo,
  '/integration/icalendar': IntegrationIcalendar,
  '/integration/icalendar-info': IntegrationIcalendarInfo,
  '/integration/airbnb': IntegrationAirbnb,
  '/integration/airbnb-info': IntegrationAirbnbInfo,
  '/integration/boxbuy': IntegrationBoxBuy,
  '/invoice/rates': TaxRates,
  '/invoice/taxes': Taxes,
  '/invoice-info': InvoiceInfo,
  '/login': Login,
  '/notifications': NotificationsService,
  '/offer/description': OfferDescription,
  '/offer/photo': OfferPhotos,
  '/offer/regulation': OfferRegulation,
  '/password': PasswordChange,
  '/reset/token/:token': PasswordResetToken,
  '/reset': PasswordReset,
  '/price/additional': PriceAdditional,
  '/price/configuration': ConfigurationPrice,
  '/price/edit': RedirectRoute('/price/set'),
  '/price/method': PriceMethod,
  '/price/prepayment': PricePrepayment,
  '/price/set': PriceGrid,
  '/profile': Profile,
  '/settings': Settings,
  '/modalcookies': CookiesModalStandaloneStep0,
  '/modalcookies-step2': CookiesModalStandaloneStep1,
  '/statistics/reservations': StatsReservations,
  '/statistics/room': RoomReport,
  '/statistics/book': BookingReport,
  '/statistics/payments': PaymentsReport,
  '/statistics/overview': Overview,
  '/statistics/local-tax-report': TaxReport,
  '/tasks': Tasks,
  '/templates': MessageTemplates,
  '/widgets': Widget,
  '/widgets/license': RentLicenses,
  '/widgets-info': WidgetInfo,
  '/widgets-old': RedirectRoute('/widgets'),
  '/integration/room': AirbnbRoomEdit,
  '/offer/facilities': Facilities,
  '/employees': Employees,
  '/employees-info': EmployeesInfo,
  '/403': AccessDenied,
  '/sso': SSOLogin,
  '/dev': protectedTemplate('UpgradePremium'),
  '/upgrade': protectedTemplate('UpgradePremium'),
  '/new-prices': PriceGridNew,
  '/integration/booking-connect-new': BookingMulti2,
  '/integration/booking-new': IntegrationBooking2,
  '/new-prices-config': ConfigurationPriceNew,
}

const NotFound = ({ location }) => {
  if (
    Object.keys(Routes).find(
      key => key === location.pathname || location.pathname.includes('reset'),
    )
  ) {
    return <></>
  }

  return <Redirect to={'/'} />
}

export default function() {
  const userId = useSelector(getUserId)
  const workerId = useSelector(getWorkerId)
  const multiPrices = useSelector(getIsMultiPriceEnabled)

  const processedRoutes = { ...Routes }

  if (multiPrices) {
    processedRoutes['/price/set'] = Routes['/new-prices']
    processedRoutes['/price/configuration'] = Routes['/new-prices-config']
    processedRoutes['/integration/booking-connect'] =
      Routes['/integration/booking-connect-new']
    processedRoutes['/integration/booking'] = Routes['/integration/booking-new']
  }

  return (
    <BrowserRouter>
      <Switch>
        <Router history={history}>
          {Object.entries(processedRoutes).map(([path, component]) => (
            <Route exact={true} path={path} component={component} />
          ))}
          <Route component={NotFound} />
        </Router>
      </Switch>
      <CreateReservationModal userId={userId as any}>
        <ReservationForms.Create workerId={workerId} />
      </CreateReservationModal>

      <UpdateReservationModal>
        <ReservationForms.Update workerId={workerId} />
      </UpdateReservationModal>
    </BrowserRouter>
  )
}

;(window as any).routerHistory = history
